import { useEffect, Suspense, lazy, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Route, Routes } from "react-router-dom";
import { Navbar } from "./components";
import ScrollToTop from "./functions/ScrollToTop";
import { Footer } from "./containers";
import { LoadingCircle } from "./components";
import { useDispatch } from "react-redux";
import { fetchAndSetStore } from "./redux/reducers/store";
import { initMetaPixel, pageView } from "./functions/metaPixel";

const Home = lazy(() => import("./pages/home/Home"));
const About = lazy(() => import("./pages/about/About"));
const ShopPage = lazy(() => import("./pages/shop/ShopPage"));
const ProductPage = lazy(() => import("./pages/product/Product"));
const Basket = lazy(() => import("./pages/basket/Basket"));
const CheckoutRoute = lazy(() => import("./pages/checkout/CheckoutRoute"));
const OrderCompleteRoute = lazy(
  () => import("./pages/orderComplete/OrderCompleteRoute"),
);
const PrivacyPolicy = lazy(() => import("./pages/privacy/Privacy"));
const Terms = lazy(() => import("./pages/terms/Terms"));
const FAQ = lazy(() => import("./pages/faq/Faq"));
const Contact = lazy(() => import("./pages/contact/Contact"));
const Returns = lazy(() => import("./pages/returns/Returns"));
const Shipping = lazy(() => import("./pages/shipping/Shipping"));

const SuspenseFallback = () => {
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(true), 180);
    return () => clearTimeout(timer);
  }, []);

  if (!showLoader) {
    return <div style={{ minHeight: 120 }} />;
  }

  return <LoadingCircle />;
};

const routes = [
  { path: "/", element: <Home /> },
  { path: "/about", element: <About /> },
  { path: "/shop", element: <ShopPage /> },
  { path: "/shop/:id", element: <ProductPage /> }, // dynamic product route
  { path: "/privacy", element: <PrivacyPolicy /> },
  { path: "/terms", element: <Terms /> },
  { path: "/faq", element: <FAQ /> },
  { path: "/contact", element: <Contact /> },
  { path: "/returns", element: <Returns /> },
  { path: "/shipping", element: <Shipping /> },
];

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState("");
  const [totalPrice, setTotalPrice] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAndSetStore());
  }, [dispatch]);

  // Track Meta Pixel page views on route change
  useEffect(() => {
    pageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    // If on /Checkout and no clientSecret, go back
    if (location.pathname === "/checkout" && !clientSecret) {
      navigate(-1);
    }
  }, [location.pathname, clientSecret, navigate]);

  useEffect(() => {
    initMetaPixel();
  }, []);

  return (
    <div className="App">
      <Navbar />
      <div>
        <ScrollToTop />
        <Suspense fallback={<SuspenseFallback />}>
          <Routes>
            {routes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
            <Route
              path="/shop/basket"
              element={
                <Basket
                  setClientSecret={setClientSecret}
                  setTotalPrice={setTotalPrice}
                />
              }
            />
            <Route
              path="/checkout"
              element={
                clientSecret ? (
                  <CheckoutRoute
                    totalPrice={totalPrice}
                    clientSecret={clientSecret}
                  />
                ) : (
                  <LoadingCircle />
                )
              }
            />
            <Route path="/order-complete" element={<OrderCompleteRoute />} />
          </Routes>
        </Suspense>
      </div>
      <Footer />
    </div>
  );
};

export default App;
